import { useCallback, useEffect, useMemo, useState } from "react";

import {
  getCampaignDetail,
  getCampaignHistory,
  retryCampaign,
} from "../services/campaignApi";

import "./HistorialTab.css";

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(String(value).replace(" ", "T"));

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("es-ES", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function getStatusInfo(status, failed = 0) {
  const normalized = String(status || "").toLowerCase();

  if (
    normalized === "sent" ||
    normalized === "enviado"
  ) {
    return {
      label: "Enviado",
      className: "success",
    };
  }

  if (
    normalized === "partial" ||
    normalized === "parcial" ||
    Number(failed) > 0
  ) {
    return {
      label: "Parcial",
      className: "partial",
    };
  }

  if (
    normalized === "failed" ||
    normalized === "error"
  ) {
    return {
      label: "Error",
      className: "error",
    };
  }

  if (
    normalized === "sending" ||
    normalized === "enviando"
  ) {
    return {
      label: "En proceso",
      className: "neutral",
    };
  }

  return {
    label: status || "Desconocido",
    className: "neutral",
  };
}

function chooseAttachmentFiles() {
  return new Promise((resolve) => {
    const input = document.createElement("input");

    input.type = "file";
    input.multiple = true;

    input.accept =
      ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.webp,.txt,.zip";

    input.addEventListener(
      "change",
      () => {
        resolve(Array.from(input.files || []));
      },
      { once: true },
    );

    input.click();
  });
}

function wait(milliseconds) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds);
  });
}

export default function HistorialTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [retryingCampaignId, setRetryingCampaignId] =
    useState(null);

  const [retryProgress, setRetryProgress] = useState(null);

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await getCampaignHistory({
        page: 1,
        pageSize: 100,
      });

      const items = Array.isArray(response)
        ? response
        : Array.isArray(response?.items)
          ? response.items
          : [];

      setCampaigns(items);
    } catch (err) {
      console.error("[HistorialTab] Error:", err);

      setError(
        err?.message ||
          "No se pudo cargar el historial de campañas.",
      );

      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return campaigns;

    return campaigns.filter((campaign) => {
      const subject = String(
        campaign?.subject || "",
      ).toLowerCase();

      const mode = String(
        campaign?.recipient_mode || "",
      ).toLowerCase();

      const status = String(
        campaign?.status || "",
      ).toLowerCase();

      return (
        subject.includes(query) ||
        mode.includes(query) ||
        status.includes(query)
      );
    });
  }, [campaigns, search]);

  async function handleView(campaignId) {
    try {
      setLoadingDetail(true);
      setError("");

      const response = await getCampaignDetail(campaignId);

      setDetail(
        response?.campaign ||
          response?.item ||
          response ||
          null,
      );
    } catch (err) {
      console.error(
        "[HistorialTab] Error cargando detalle:",
        err,
      );

      setError(
        err?.message ||
          "No se pudo cargar el detalle de la campaña.",
      );
    } finally {
      setLoadingDetail(false);
    }
  }

  async function handleCopy(campaign) {
    try {
      const response = await getCampaignDetail(campaign.id);

      const campaignDetail =
        response?.campaign ||
        response?.item ||
        response ||
        campaign;

      const text = [
        campaignDetail?.subject
          ? `Asunto: ${campaignDetail.subject}`
          : "",
        campaignDetail?.body || "",
      ]
        .filter(Boolean)
        .join("\n\n");

      await navigator.clipboard.writeText(text);

      window.alert(
        "El contenido de la campaña se ha copiado.",
      );
    } catch (err) {
      console.error(
        "[HistorialTab] Error copiando campaña:",
        err,
      );

      window.alert(
        "No se pudo copiar el contenido de la campaña.",
      );
    }
  }

  async function handleRetryCampaign(campaign) {
    const total = Number(campaign.total || 0);
    const sent = Number(campaign.sent || 0);

    const confirmed = window.confirm(
      [
        "Se enviarán únicamente los correos que NO constan como enviados.",
        "",
        `Campaña: ${campaign.subject || "Sin asunto"}`,
        `Confirmados como enviados: ${sent}/${total}`,
        "",
        "Los correos ya enviados no se repetirán.",
        "",
        "¿Quieres continuar?",
      ].join("\n"),
    );

    if (!confirmed) {
      return;
    }

    const hadAttachments = window.confirm(
      [
        "¿La campaña original llevaba archivos adjuntos?",
        "",
        "Pulsa Aceptar si llevaba adjuntos para seleccionarlos de nuevo.",
        "Pulsa Cancelar si NO llevaba adjuntos.",
      ].join("\n"),
    );

    let attachments = [];

    if (hadAttachments) {
      attachments = await chooseAttachmentFiles();

      if (attachments.length === 0) {
        window.alert(
          "No has seleccionado ningún archivo. No se ha iniciado el reenvío.",
        );

        return;
      }
    }

    try {
      setRetryingCampaignId(campaign.id);
      setRetryProgress({
        sent: sent,
        total: total,
        remaining: Math.max(0, total - sent),
      });

      setError("");

      let lastResponse = null;

      /*
       * Cada llamada a campaign.retry.php procesa solo una tanda
       * de 8 correos. Repetimos llamadas cortas para evitar un
       * único PHP de varios minutos que termine en 504.
       */
      for (let batchNumber = 1; batchNumber <= 100; batchNumber += 1) {
        const response = await retryCampaign(
          campaign.id,
          {
            attachments,
          },
        );

        lastResponse = response;

        setRetryProgress({
          sent: Number(response?.sent_total || 0),
          total: Number(response?.total || 0),
          remaining: Number(response?.remaining || 0),
        });

        if (!response?.has_more) {
          break;
        }

        /*
         * Si una tanda completa no consigue enviar ni un solo
         * correo, detenemos el proceso. Así evitamos un bucle
         * infinito si el SMTP vuelve a rechazar la autenticación.
         */
        if (response?.made_progress === false) {
          window.alert(
            [
              "El servidor no ha conseguido enviar ningún correo en la última tanda.",
              "",
              `Quedan ${Number(response?.remaining || 0)} pendientes.`,
              "",
              "El proceso se ha detenido para evitar reintentos continuos. Puedes volver a intentarlo más tarde.",
            ].join("\n"),
          );

          break;
        }

        /*
         * Pausa corta entre peticiones para aliviar tanto Nginx
         * como el servidor SMTP.
         */
        await wait(600);
      }

      await loadHistory();

      if (lastResponse) {
        const remaining = Number(
          lastResponse.remaining || 0,
        );

        if (remaining === 0) {
          window.alert(
            "Campaña completada. Todos los destinatarios constan ahora como enviados.",
          );
        } else if (lastResponse.made_progress !== false) {
          window.alert(
            `El proceso ha terminado con ${remaining} destinatarios todavía pendientes.`,
          );
        }
      }
    } catch (err) {
      console.error(
        "[HistorialTab] Error continuando campaña:",
        err,
      );

      setError(
        err?.message ||
          "No se pudo continuar la campaña.",
      );

      window.alert(
        err?.message ||
          "No se pudo continuar la campaña.",
      );
    } finally {
      setRetryingCampaignId(null);
      setRetryProgress(null);
    }
  }

  return (
    <div className="history-page">
      <div className="history-toolbar">
        <div className="history-search">
          <i
            className="fa-solid fa-magnifying-glass"
            aria-hidden="true"
          />

          <input
            type="search"
            placeholder="Buscar campaña..."
            value={search}
            onChange={(event) =>
              setSearch(event.target.value)
            }
          />

          {search && (
            <button
              type="button"
              aria-label="Borrar búsqueda"
              onClick={() => setSearch("")}
            >
              <i
                className="fa-solid fa-xmark"
                aria-hidden="true"
              />
            </button>
          )}
        </div>

        <button
          type="button"
          className="history-refresh"
          onClick={loadHistory}
          disabled={loading || retryingCampaignId !== null}
        >
          <i
            className={`fa-solid fa-rotate ${
              loading ? "fa-spin" : ""
            }`}
            aria-hidden="true"
          />

          {loading ? "Actualizando..." : "Actualizar"}
        </button>
      </div>

      {error && (
        <div className="history-error">
          <i
            className="fa-solid fa-circle-exclamation"
            aria-hidden="true"
          />
          <span>{error}</span>
        </div>
      )}

      {retryProgress && (
        <div className="history-retry-progress">
          <i
            className="fa-solid fa-paper-plane"
            aria-hidden="true"
          />

          <span>
            Continuando campaña:{" "}
            <strong>
              {retryProgress.sent}/{retryProgress.total}
            </strong>{" "}
            enviados · {retryProgress.remaining} pendientes
          </span>
        </div>
      )}

      <div className="history-table">
        <div className="history-head">
          <div>Asunto</div>
          <div>Fecha</div>
          <div>Destinatarios</div>
          <div>Estado</div>
          <div>Acciones</div>
        </div>

        {loading && campaigns.length === 0 && (
          <div className="history-empty">
            <i
              className="fa-solid fa-spinner fa-spin"
              aria-hidden="true"
            />

            <h3>Cargando historial...</h3>
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="history-empty">
            <i
              className="fa-solid fa-envelope-open"
              aria-hidden="true"
            />

            <h3>
              {search
                ? "No hay campañas que coincidan"
                : "Todavía no has enviado campañas"}
            </h3>

            <p>
              {search
                ? "Prueba con otra búsqueda."
                : "Cuando envíes un correo aparecerá aquí."}
            </p>
          </div>
        )}

        {filtered.map((campaign) => {
          const status = getStatusInfo(
            campaign.status,
            campaign.failed,
          );

          const campaignTotal =
            Number(campaign.total || 0);

          const campaignSent =
            Number(campaign.sent || 0);

          const incomplete =
            campaignTotal > campaignSent ||
            Number(campaign.failed || 0) > 0 ||
            [
              "partial",
              "parcial",
              "sending",
              "enviando",
              "failed",
              "error",
            ].includes(
              String(campaign.status || "").toLowerCase(),
            );

          const isRetrying =
            retryingCampaignId === campaign.id;

          return (
            <div
              key={campaign.id}
              className="history-row"
            >
              <div>
                <strong>
                  {campaign.subject || "Sin asunto"}
                </strong>

                {Number(campaign.attachments || 0) > 0 && (
                  <span className="attachment">
                    <i
                      className="fa-solid fa-paperclip"
                      aria-hidden="true"
                    />
                    {campaign.attachments}
                  </span>
                )}
              </div>

              <div>
                {formatDate(
                  campaign.created_at ||
                    campaign.finished_at,
                )}
              </div>

              <div>
                {campaignSent}/{campaignTotal}
              </div>

              <div>
                <span
                  className={`status ${status.className}`}
                >
                  {status.label}
                </span>
              </div>

              <div className="actions">
                <button
                  type="button"
                  title="Ver campaña"
                  aria-label="Ver campaña"
                  onClick={() =>
                    handleView(campaign.id)
                  }
                  disabled={
                    loadingDetail ||
                    retryingCampaignId !== null
                  }
                >
                  <i
                    className="fa-solid fa-eye"
                    aria-hidden="true"
                  />
                </button>

                <button
                  type="button"
                  title="Copiar contenido"
                  aria-label="Copiar contenido"
                  onClick={() =>
                    handleCopy(campaign)
                  }
                  disabled={retryingCampaignId !== null}
                >
                  <i
                    className="fa-solid fa-copy"
                    aria-hidden="true"
                  />
                </button>

                {incomplete && (
                  <button
                    type="button"
                    title="Continuar pendientes"
                    aria-label="Continuar pendientes"
                    onClick={() =>
                      handleRetryCampaign(campaign)
                    }
                    disabled={retryingCampaignId !== null}
                  >
                    <i
                      className={`fa-solid fa-rotate-right ${
                        isRetrying ? "fa-spin" : ""
                      }`}
                      aria-hidden="true"
                    />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {detail && (
        <div
          className="history-modal-backdrop"
          onClick={() => setDetail(null)}
          role="presentation"
        >
          <div
            className="history-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="history-detail-title"
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            <div className="history-modal-header">
              <div>
                <span className="history-modal-kicker">
                  Campaña #{detail.id}
                </span>

                <h3 id="history-detail-title">
                  {detail.subject || "Sin asunto"}
                </h3>
              </div>

              <button
                type="button"
                aria-label="Cerrar"
                onClick={() => setDetail(null)}
              >
                <i
                  className="fa-solid fa-xmark"
                  aria-hidden="true"
                />
              </button>
            </div>

            <div className="history-modal-meta">
              <span>
                <strong>Fecha:</strong>{" "}
                {formatDate(
                  detail.created_at ||
                    detail.finished_at,
                )}
              </span>

              <span>
                <strong>Enviados:</strong>{" "}
                {Number(detail.sent || 0)}/
                {Number(detail.total || 0)}
              </span>

              <span>
                <strong>Fallidos:</strong>{" "}
                {Number(detail.failed || 0)}
              </span>
            </div>

            <div className="history-modal-message">
              <h4>Mensaje</h4>

              <div>
                {String(detail.body || "")
                  .split("\n")
                  .map((line, index) =>
                    line ? (
                      <p key={`${line}-${index}`}>
                        {line}
                      </p>
                    ) : (
                      <br key={`br-${index}`} />
                    ),
                  )}
              </div>
            </div>

            {Array.isArray(detail.recipients) &&
              detail.recipients.length > 0 && (
                <div className="history-modal-recipients">
                  <h4>
                    Destinatarios (
                    {detail.recipients.length})
                  </h4>

                  <div>
                    {detail.recipients.map(
                      (recipient, index) => (
                        <div
                          key={`${recipient.email}-${index}`}
                          className="history-recipient-row"
                        >
                          <span>
                            <strong>
                              {recipient.name ||
                                "Sin nombre"}
                            </strong>

                            <small>
                              {recipient.email}
                            </small>
                          </span>

                          <span
                            className={`status ${
                              recipient.sent_at
                                ? "success"
                                : "error"
                            }`}
                          >
                            {recipient.sent_at
                              ? "Enviado"
                              : "Pendiente"}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
}